import InputDatePicker from '../Input/InputDatePicker';
import { myHistoryCodeVo } from '@/__generated__/FrontApi';
import Button from '@/components/Button';
import FormGroup from '@/components/Form/FormGroup';
import Label from '@/components/Form/Label';
import InputRadio, { InputRadioGroup } from '@/components/Input/InputRadio';
import Select from '@/components/Select';
import { breakpoint } from '@/helpers/BreakpointHelper';
import { usePopupStore } from '@/stores/PopupStore';
import dayjs from 'dayjs';
import React, { FC, useCallback, useState } from 'react';
import styled from 'styled-components';

const SupportSearchBox = styled.div`
  background: #f8f9fa;
  border: 1px solid #e5e6e8;
  padding: 32px 48px;
  position: relative;
  margin-top: 32px;
  ${breakpoint(640)} {
    padding: 24px;
  }
`;
const RadioLabel = styled.div`
  display: table-cell;
  cursor: pointer;
  .btn {
    border-left: none;
    transition: all 0.25s;
    background: #fff;
    &:hover {
      background: #1cabe2;
      color: #fff;
    }
  }
`;
const RadioFlex = styled(InputRadioGroup)`
  border-left: 1px solid #1cabe2;
  div {
    height: 56px;
    line-height: 54px;
  }
  label {
    vertical-align: top;
  }
`;
const DateFlex = styled.div`
  display: flex;
  .blank {
    display: inline-block;
    vertical-align: middle;
    line-height: 56px;
  }
  ${breakpoint(640)} {
    .input-date {
      width: 45%;
      display: inline-block;
    }
    .blank {
      width: 10%;
      text-align: center;
    }
  }
`;
const Flex = styled.div`
  display: flex;
  width: 100%;
  ${breakpoint(991)} {
    flex-wrap: wrap;
  }
  ${FormGroup} {
    &.form_period {
      width: 352px;
      flex-shrink: 0;
      ${breakpoint(991)} {
        width: 100%;
      }
    }
  }
`;
const ButtonContainer = styled.div``;
const SelectCategory = styled(FormGroup)`
  width: 352px;
  flex-shrink: 0;
  ${breakpoint(991)} {
    width: 100%;
  }
`;
const SelectDate = styled(FormGroup)`
  padding-top: 28px;
  margin-left: 64px;
  margin-bottom: 0;
  display: flex;
  ${breakpoint(1024)} {
    margin-left: 16px;
  }
  ${breakpoint(991)} {
    padding-top: 0;
    margin-left: 0;
  }
  ${breakpoint(640)} {
    flex-wrap: wrap;
    ${ButtonContainer} {
      width: 100%;
    }
  }
  p {
    font-size: 14px;
    font-weight: 300;
    margin-top: 6px;
    ${breakpoint(640)} {
      font-size: 12px;
    }
  }
  ${Button} {
    width: 160px;
    margin-left: 32px;
    background: #fff;
    ${breakpoint(1024)} {
      margin-left: 16px;
    }
    ${breakpoint(991)} {
      margin-top: 0;
    }
    ${breakpoint(640)} {
      width: 100%;
      margin: 40px 0 0 0;
    }
  }
  .blank {
    padding: 0 16px;
    ${breakpoint(640)} {
      padding: 0;
    }
  }
`;

interface SupportSearchProps {
  codes: myHistoryCodeVo[];
  startDate?: dayjs.Dayjs;
  endDate?: dayjs.Dayjs;
  onCodeChange: (code: string) => void;
  onPeriodChange: (start?: dayjs.Dayjs, end?: dayjs.Dayjs) => void;
}

const SupportSearch: FC<SupportSearchProps> = ({
  codes,
  startDate,
  endDate,
  onPeriodChange,
  onCodeChange,
}) => {
  const popupStore = usePopupStore();
  const limitDate = dayjs().add(-5, `year`);
  const [start, setStart] = useState<dayjs.Dayjs>(startDate || limitDate);
  const [end, setEnd] = useState<dayjs.Dayjs>(endDate || dayjs());

  const onSearch = useCallback(() => {
    if (start.diff(limitDate, `day`) < 0) {
      popupStore.show(
        '원활한 서비스 이용을 위해\n 최근 5년 내역만 조회 가능 합니다.',
      );
      setStart(limitDate);
      return;
    }
    onPeriodChange(start, end);
  }, [start, end, onPeriodChange, setStart]);

  return (
    <SupportSearchBox>
      <SelectCategory>
        <FormGroup>
          <Label>구분</Label>
          <Select name="glCode" onChange={(e) => onCodeChange(e.target.value)}>
            <option value="">전체</option>
            {codes.map((code) => (
              <option value={code.glCode} key={code.glCode}>
                {code.description}
              </option>
            ))}
          </Select>
        </FormGroup>
      </SelectCategory>
      <Flex>
        <FormGroup className="form_period">
          <Label>기간</Label>
          <RadioFlex>
            <RadioLabel>
              <InputRadio
                tab
                label="전체"
                value="전체"
                name="periodRadio"
                defaultChecked
                onChange={(e) => {
                  if (e.target.checked) {
                    const newStart = dayjs().add(-5, `year`);
                    const newEnd = dayjs();
                    setStart(newStart);
                    setEnd(newEnd);
                    onPeriodChange(newStart, newEnd);
                  }
                }}
              />
            </RadioLabel>
            <RadioLabel>
              <InputRadio
                tab
                label="1개월"
                value="1개월"
                name="periodRadio"
                onChange={(e) => {
                  if (e.target.checked) {
                    const newStart = dayjs().add(-1, `month`);
                    const newEnd = dayjs();
                    setStart(newStart);
                    setEnd(newEnd);
                    onPeriodChange(newStart, newEnd);
                  }
                }}
              />
            </RadioLabel>
            <RadioLabel>
              <InputRadio
                tab
                label="3개월"
                value="3개월"
                name="periodRadio"
                onChange={(e) => {
                  if (e.target.checked) {
                    const newStart = dayjs().add(-3, `month`);
                    const newEnd = dayjs();
                    setStart(newStart);
                    setEnd(newEnd);
                    onPeriodChange(newStart, newEnd);
                  }
                }}
              />
            </RadioLabel>
            <RadioLabel>
              <InputRadio
                tab
                label="6개월"
                value="6개월"
                name="periodRadio"
                onChange={(e) => {
                  if (e.target.checked) {
                    const newStart = dayjs().add(-6, `month`);
                    const newEnd = dayjs();
                    setStart(newStart);
                    setEnd(newEnd);
                    onPeriodChange(newStart, newEnd);
                  }
                }}
              />
            </RadioLabel>
          </RadioFlex>
        </FormGroup>

        <SelectDate>
          <div>
            <DateFlex>
              <InputDatePicker
                className="input-date"
                onChange={(value) =>
                  value && !Array.isArray(value) && setStart(dayjs(value))
                }
                selected={start?.toDate()}
                maxDate={end?.toDate() || new Date()}
              />
              <span className="blank">~</span>
              <InputDatePicker
                className="input-date"
                onChange={(value) =>
                  value && !Array.isArray(value) && setEnd(dayjs(value))
                }
                selected={end?.toDate()}
                minDate={start?.toDate()}
                maxDate={new Date()}
              />
            </DateFlex>
            <p>* 원활한 서비스 이용을 위해 최근 5년 내역만 조회 가능 합니다.</p>
          </div>
          <ButtonContainer>
            <Button outline onClick={onSearch}>
              검색
            </Button>
          </ButtonContainer>
        </SelectDate>
      </Flex>
    </SupportSearchBox>
  );
};

export default SupportSearch;
